import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Tab, Tabs } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { getDDAStatus, getNewLoadQaVat, putArrivalDate, putEPO, getEPO } from '../../apiCalls';
import { useSecurity } from '../../Security/security';
import { UpdateEtaModal } from '../UpdateEtaModal';
import { Confirmation } from './Tabs/Confirmation';
import EpoJobCombinedInvoices from './Tabs/EpoJobCombinedInvoices';
import EpoJobHeaders from './Tabs/EpoJobHeaders';
import EpoJobInvoices from './Tabs/EpoJobInvoices';
import EpoJobStatus from './Tabs/EpoJobStatus';
import EpoJobSummary from './Tabs/EpoJobSummary';
import { getDutyRatesFromItems, getIsVatLiable, mergeEpoDutyCheckRows } from '../../Newload/QAChecklistTab/utils';
import EpoDutyCheckModal from './Tabs/EpoDutyCheckModal';
import { StoreContext } from '../../Store';
import { getCompanyNameFromId } from '../../../api/newLoad';
//the documents and summary tabs have errors when loaded first
const startingTabOptions = ['headers', 'status', 'receipt'];

export const EpoJobDetailContext = createContext();

const getEpoStatusPermissions = (epoStatus, isSuper) => {
  switch (epoStatus) {
    case 'New':
      return { canSave: true, canSubmit: false, canEditInvoiceData: true };
    case 'In progress':
    case 'Ready to be checked':
    case 'Ready to be checked (Potential Duty)':
    case 'On Hold (Potential Duty)':
      return { canSave: true, canSubmit: true, canEditInvoiceData: true };
    case 'Waiting for Customer':
      return { canSave: true };
    case 'Awaiting Proof of Payment':
      return { canSubmit: true };
    case 'On Hold (Awaiting Duty Invoice)':
      return isSuper ? { canSubmit: true, canSave: true, canEditInvoiceData: true } : {};
    case 'Awaiting Payment Confirmation':
      return isSuper ? { canSubmit: true, canSave: true } : {};
    default:
      return {};
  }
};

const computeJobPermissions = ({ jobDetails, confirmed, serverData, validateRole }) => {
  const base = { canSubmit: true, canSave: true, canEditInvoiceData: false };

  if (jobDetails?.EPOStatus === 'Submitted') {
    return { ...base, canSave: false, canSubmit: false };
  }

  const isInProgress = confirmed || serverData.confirmed;
  if (isInProgress) {
    return { ...base, canSave: false, canSubmit: false };
  }

  const isOpsOrQa = validateRole({ department: 'OPS' }) || validateRole({ department: 'QA' });
  if (!isOpsOrQa) return base;

  const isSuper =
    validateRole({ department: 'OPS', role: 'SUPERUSER' }) || validateRole({ department: 'QA', role: 'SUPERUSER' });

  const override = getEpoStatusPermissions(jobDetails?.EPOStatus, isSuper);
  return { canSave: false, canSubmit: false, canEditInvoiceData: false, ...override };
};

const EpoJobDetails = () => {
  const { id } = useParams();
  const { validateRole } = useSecurity();
  const store = useContext(StoreContext);
  const [jobDetails, setJobDetails] = useState({});
  const [invoiceDetails, setInvoiceDetails] = useState([]);
  const [invoiceId, setInvoiceId] = useState(0);
  const [serverData, setServerData] = useState({});
  const navigate = useNavigate();

  const [modalItem, setModalItem] = useState(null);
  const [error, setError] = useState();
  const [openedInvoice, setOpenedInvoice] = useState(null);
  const [showUpdateEta, setShowUpdateEta] = useState(false);
  const [showArrivalModal, setShowArrivalModal] = useState(false);
  const [successText, setSuccessText] = useState('');
  const [adjustmentsIncoLoaded, setAdjustmentsIncoLoaded] = useState(false);
  const [showAdjustmentsModal, setShowAdjustmentsModal] = useState(false);
  const [isFreightChargesInco, setIsFreightChargesInco] = useState(false);
  const [checkForm, setCheckForm] = useState(false);
  const formRef = useRef();

  const previousTab = sessionStorage.getItem(`epoTab${id}`);
  const startingTab = startingTabOptions.includes(previousTab) ? previousTab : 'headers';

  const [tab, setTab] = useState(startingTab);
  const [sumInvoice, setSumInvoice] = useState(0);
  const [firstHeadLoad, setFirstHeadLoad] = useState(0);
  const [freightErrorState, setFreightErrorState] = useState();
  const [confirmed, setConfirmed] = useState(false);
  const [showBulkBtn, setShowBulkBtn] = useState(false);
  const [fileBuffer, setBuffer] = useState(null);
  const [cycle, setCycle] = useState(false);
  const [DDAStatus, setDDAStatus] = useState(0);

  // calculating if duty is applicable
  const [loadingDutyData, setLoadingDutyData] = useState(false);
  const [hasExciseGoods, setHasExciseGoods] = useState(false);
  const [hasOtherDuty, setHasOtherDuty] = useState(false);
  const [hasDutyLiability, setHasDutyLiability] = useState(false);
  const [hasVatLiability, setHasVatLiability] = useState(false);
  const [vatType, setVatType] = useState('');
  const [dutyRates, setDutyRates] = useState([]);
  const [loadIsReadOnly, setLoadIsReadOnly] = useState(false);

  const [dutyCheckModalOpen, setDutyCheckModalOpen] = useState(false);
  const [dutyCheckRows, setDutyCheckRows] = useState([]);
  const [dutyCheckNoDutyDetected, setDutyCheckNoDutyDetected] = useState(false);
  const [dutyCheckLoadFailed, setDutyCheckLoadFailed] = useState(false);
  const [dutyCheckLoading, setDutyCheckLoading] = useState(false);

  const isPva = vatType === 'PVA';
  const potentialLiability = isPva ? hasDutyLiability : hasDutyLiability || hasVatLiability;
  const hasDutyOrVatOrExcise = potentialLiability || hasExciseGoods || hasOtherDuty;

  const updateJobDetails = (key, val) => setJobDetails((prev) => ({ ...prev, [key]: val }));

  async function fetchDetails() {
    try {
      const data = await getEPO(id, 'Header');
      setLoadIsReadOnly(!!data?.Parents?.[0]?.readOnly);
      setJobDetails({ id, ...data.Parents[0] });
      setServerData({ id, ...data.Parents[0] });
      setAdjustmentsIncoLoaded(true);
      setFirstHeadLoad(1);
      const companyId = data.Parents[0].companyId;
      const ddaStatus = await getDDAStatus(companyId);
      if (ddaStatus) {
        setDDAStatus(ddaStatus[0]?.DDA);
      }
    } catch (err) {
      console.error('jobDetails load error', err);
    }
  }

  const updateTab = (tab) => {
    sessionStorage.setItem(`epoTab${id}`, tab);
    setTab(tab);
  };

  useEffect(() => {
    fetchDetails();
  }, [id]);

  useEffect(() => {
    const fetchBillableType = async () => {
      try {
        if (!jobDetails?.companyId) return;
        const companyInfo = await getCompanyNameFromId(jobDetails.companyId);
        const billableType = companyInfo?.[0]?.billableType ?? 0;
        setJobDetails((prev) => ({ ...prev, billableType }));
        store?.billableType?.[1]?.(billableType);
      } catch (err) {
        console.error('Error fetching company billable type', err);
      }
    };
    fetchBillableType();
  }, [jobDetails?.companyId, store?.billableType, store?.clientIDname]);

  useEffect(() => {
    async function fetchDetails() {
      if (tab !== 'headers' || firstHeadLoad !== 1) return;
      try {
        const data = await getEPO(id, 'Header');
        if (data) {
          setJobDetails({ id, ...data.Parents[0] });
          setServerData({ id, ...data.Parents[0] });
          setLoadIsReadOnly(!!data?.Parents?.[0]?.readOnly);
        }
        setAdjustmentsIncoLoaded(true);
      } catch (err) {
        console.error('Header error: ', err);
      }
    }
    fetchDetails();
  }, [tab]);

  const handleSaveHeaders = async () => {
    const { adjustments } = jobDetails;
    const hasEmptyCurrency = adjustments.some(
      ({ amount, currency, name }) => amount && !currency && name !== 'freightCharges'
    );
    const fc = jobDetails.adjustments.find((adj) => adj.name === 'freightCharges');

    if (hasEmptyCurrency) {
      updateJobDetails(
        'adjustments',
        jobDetails.adjustments.map((adjustment) => {
          if (adjustment.amount && !adjustment.currency && adjustment.name !== 'freightCharges')
            adjustment.required = true;
          return adjustment;
        })
      );

      setShowAdjustmentsModal(true);
      jobDetails.adjustments.filter(({ amount, currency, required, name }) => {
        if (amount && !currency && required && name !== 'freightCharges') setCheckForm(true);
      });
    } else if (!hasEmptyCurrency && isFreightChargesInco && (fc.amount === '' || fc.currency === '' || fc.amount < 1)) {
      if (fc.amount === '' || fc.currency === '') {
        setShowAdjustmentsModal(true);
        setCheckForm(true);
      } else if (fc.amount < 1) {
        setFreightErrorState('loading');
        updateJobDetails(
          'adjustments',
          jobDetails.adjustments.map((adjustment) => {
            if (adjustment.name === 'freightCharges') adjustment.error = 'Must be greater than 0.';
            return adjustment;
          })
        );
      }
    } else {
      const result = await putEPO(
        {
          currency: jobDetails.currency,
          incoterm: jobDetails.incoterm,
          consignorId: jobDetails.consignor,
          countryOfDespatchId: jobDetails.despatchCountry,
          id: jobDetails.newLoadId,
          isImport: jobDetails.isImport ? 1 : 0,
          adjustments: jobDetails.adjustments,
          repConsigneeId: jobDetails.repConsignee,
        },
        'Header',
        jobDetails.newLoadId
      );
      if (result[0].completed === 'Success') {
        setSuccessText('Successfully saved.');
        const data = await getEPO(id, 'Header');
        setJobDetails({ id, ...data.Parents[0] });
        setServerData({ id, ...data.Parents[0] });
        setLoadIsReadOnly(!!data?.Parents?.[0]?.readOnly);
        setAdjustmentsIncoLoaded(true);
      }
    }
  };

  useEffect(() => {
    if (checkForm) {
      setTimeout(() => {
        formRef.current.reportValidity();
        setCheckForm(false);
      }, 100);
    }
  }, [checkForm]);

  useEffect(() => {
    if (freightErrorState === 'loading') {
      if (
        jobDetails.adjustments.reduce((acc, cur) => {
          return acc || !!cur.error;
        }, false)
      )
        setFreightErrorState('loaded');
    }
  }, [jobDetails?.adjustments, freightErrorState]);

  useEffect(() => {
    if (freightErrorState === 'loaded') {
      setFreightErrorState();
      setShowAdjustmentsModal(true);
    }
  }, [freightErrorState]);

  const handleUpdateArrivalDate = async ({ date }) => {
    await putArrivalDate({
      id: jobDetails.newLoadId,
      newArrivalDate: date,
    });
    const data = await getEPO(id, 'Header');
    setJobDetails({ id, ...data.Parents[0] });
    setLoadIsReadOnly(!!data?.Parents?.[0]?.readOnly);
    setAdjustmentsIncoLoaded(true);

    setSuccessText('Successfully updated.');
  };

  useEffect(() => {
    setSuccessText('');
  }, [tab]);

  const confirmEPO = () => {
    setConfirmed(true);
    setTab('status');
  };

  const handleDutyCheckClick = async () => {
    if (!jobDetails.newLoadId) return;
    setDutyCheckLoading(true);
    setDutyCheckLoadFailed(false);
    try {
      const epoSummaryItems = await getEPO(jobDetails.newLoadId, 'summarytab');
      const items = epoSummaryItems.Newload[0]?.Items || [];
      const dutyRatesResult = await getDutyRatesFromItems(items, 1);
      if (dutyRatesResult.error) throw new Error('Duty rates lookup failed');
      const rows = mergeEpoDutyCheckRows(items, dutyRatesResult.data);
      setDutyCheckRows(rows);
      setDutyCheckNoDutyDetected(rows.length === 0);
      setDutyCheckModalOpen(true);
    } catch (err) {
      console.error('Duty check failed', err);
      setDutyCheckRows([]);
      setDutyCheckNoDutyDetected(false);
      setDutyCheckLoadFailed(true);
      setDutyCheckModalOpen(true);
    } finally {
      setDutyCheckLoading(false);
    }
  };

  useEffect(() => {
    const getItemsAndDuties = async () => {
      setLoadingDutyData(true);
      try {
        const epoSummaryItems = await getEPO(jobDetails.newLoadId, 'summarytab');
        const items = epoSummaryItems.Newload[0]?.Items || [];

        //we don't need to know price, so set exchange rate to one
        const dutyRates = await getDutyRatesFromItems(items, 1);
        if (dutyRates.error) {
          console.error('Error retrieving duty rate data');
          setDutyRates([]);
        } else {
          setDutyRates(dutyRates.data);

          const hasAnyVat = items.some((item) => getIsVatLiable(Number.parseInt(item.VAT)));

          setHasVatLiability(hasAnyVat);
        }
      } catch (error) {
        console.error('error fetching EPO summarytab data - ', error);
      }
      setLoadingDutyData(false);
    };

    if (jobDetails.newLoadId) {
      getItemsAndDuties();
    }
  }, [jobDetails.newLoadId, vatType]);

  useEffect(() => {
    setHasExciseGoods(dutyRates.some((duty) => !!duty.excise));
    setHasOtherDuty(dutyRates.some((duty) => !!duty.additionalDuties));
    setHasDutyLiability(dutyRates.some((duty) => !!duty.dutyLiability));
  }, [dutyRates]);

  useEffect(() => {
    const fetchVatData = async () => {
      const data = await getNewLoadQaVat(jobDetails.consignee);
      setVatType(data?.[0]?.vatMessage);
    };

    jobDetails.consignee && fetchVatData();
  }, [jobDetails.consignee]);

  const isCustomer = validateRole({ isCustomer: true });

  const { canSubmit, canSave, canEditInvoiceData } = computeJobPermissions({
    jobDetails,
    confirmed,
    serverData,
    validateRole,
  });

  if (jobDetails === null) return null;

  return (
    <EpoJobDetailContext.Provider
      value={{
        jobDetails,
        setJobDetails,
        serverData,
        setServerData,
        openedInvoice,
        setOpenedInvoice,
        tab,
        setTab,
        updateJobDetails,
        canSave,
        isCustomer,
        setShowUpdateEta,
        setShowArrivalModal,
        showArrivalModal,
        setModalItem,
        modalItem,
        setError,
        error,
        setSuccessText,
        adjustmentsIncoLoaded,
        setAdjustmentsIncoLoaded,
        showAdjustmentsModal,
        setShowAdjustmentsModal,
        isFreightChargesInco,
        setIsFreightChargesInco,
        formRef,
        confirmEPO,
        tab,
        invoiceDetails,
        setInvoiceDetails,
        sumInvoice,
        setSumInvoice,
        invoiceId,
        setInvoiceId,
        showBulkBtn,
        setShowBulkBtn,
        fileBuffer,
        setBuffer,
        cycle,
        setCycle,
        hasDutyOrVatOrExcise,
        loadingDutyData,
        loadIsReadOnly,
      }}
    >
      <UpdateEtaModal
        newArrivalDate={jobDetails.newArrivalDate}
        estimatedDateOfLoading={jobDetails.estimatedDateOfLoading}
        show={showUpdateEta}
        onHide={() => setShowUpdateEta(false)}
        onConfirm={handleUpdateArrivalDate}
      />

      <EpoDutyCheckModal
        show={dutyCheckModalOpen}
        onHide={() => setDutyCheckModalOpen(false)}
        loadFailed={dutyCheckLoadFailed}
        noDutyDetected={dutyCheckNoDutyDetected}
        rows={dutyCheckRows}
      />

      <div className="full-width-container">
        <div className="align-left" style={{ maxWidth: '1600px' }}>
          <div className="themed-text" style={{ fontSize: '26px' }}>
            <span style={{ fontSize: '22px' }}>Reference No: </span> {jobDetails?.TransferID}
          </div>
        </div>
        <section className="floating-box" style={{ maxWidth: '1600px' }}>
          <Tabs activeKey={tab} onSelect={(newTab) => updateTab(newTab)} style={{ paddingTop: '0.8rem' }}>
            <Tab eventKey="headers" title="Headers">
              <div className="p-3 bg-white">
                <EpoJobHeaders editInvoiceData={canEditInvoiceData} />
              </div>
            </Tab>

            {!isCustomer && (
              <Tab eventKey="invoices" title="Documents">
                <div className="p-3 bg-white">
                  <EpoJobInvoices />
                </div>
              </Tab>
            )}

            <Tab eventKey="items" title="Summary">
              <div className="p-3 bg-white">
                <EpoJobCombinedInvoices />
              </div>
            </Tab>

            {/* CONFIRMATION SCREEN */}
            {canSubmit &&
              !isCustomer &&
              !serverData.confirmed &&
              serverData.consignor &&
              serverData.despatchCountry &&
              serverData.currency &&
              serverData.incoterm &&
              jobDetails?.hasItems === 'true' && (
                <Tab eventKey="confirm" title="Confirmation">
                  <div className="p-3 bg-white">
                    <EpoJobSummary />
                  </div>
                </Tab>
              )}

            {(confirmed || serverData.confirmed) && (
              <Tab eventKey="status" title="Status">
                <div className="p-3 bg-white">
                  <EpoJobStatus />
                </div>
              </Tab>
            )}

            <Tab eventKey="receipt" title="Receipt">
              <div className="p-3 bg-white">
                <Confirmation DDAStatus={DDAStatus} />
              </div>
            </Tab>
          </Tabs>

          <div
            class="d-flex flex-row py-3 px-3 gap-3 border-top bg-light justify-content-between align-items-center"
            style={{ borderBottomLeftRadius: '20px', borderBottomRightRadius: '20px' }}
          >
            <div className="d-flex flex-row gap-2 align-items-center">
              <button
                type="button"
                class="generic-btn bg-grey"
                onClick={() => navigate('/epo/loads')}
                style={{ display: 'flex', borderRadius: '10px' }}
              >
                Back
              </button>
              {tab === 'items' && (
                <button
                  type="button"
                  className="generic-btn green-btn"
                  onClick={handleDutyCheckClick}
                  disabled={dutyCheckLoading || !jobDetails.newLoadId}
                  style={{ borderRadius: '10px', display: 'flex' }}
                >
                  {dutyCheckLoading ? 'Loading…' : 'Duty Check'}
                </button>
              )}
            </div>
            <div>
              <span className="text-success bold me-3">{successText}</span>
              {tab === 'headers' && canSave && !isCustomer && (
                <button
                  disabled={
                    !jobDetails.consignor ||
                    !jobDetails.despatchCountry ||
                    !jobDetails.currency ||
                    !jobDetails.incoterm ||
                    (jobDetails.isImport && !jobDetails.consignee && !jobDetails.repConsignee) ||
                    loadIsReadOnly
                  }
                  type="button"
                  class="generic-btn green-btn save-btn"
                  style={{ borderRadius: '10px' }}
                  onClick={handleSaveHeaders}
                >
                  Save
                </button>
              )}
            </div>
          </div>
        </section>
      </div>
    </EpoJobDetailContext.Provider>
  );
};

export default EpoJobDetails;
